import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { DEFAULT_NGX_DATATABLE_CONFIG } from 'src/app/core/helpers/ui/ngx-datatable.constant';
import {
  NgxDatatableConfig,
  NgxDatatableSort,
  NgxDatatableColumn,
} from 'src/app/core/interfaces/ui/ngx-datatable.interface';
import { MobileCardAction } from 'src/app/core/interfaces/ui/ngx-datatable-mobile.interface';
import { RESPONSIVE_CONFIG } from 'src/app/core/helpers/ui/toogle-panel.constant';

@Component({
  selector: 'shared-tables-ngx-datatable',
  templateUrl: './ngx-datatable.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NgxDatatableComponent {
  public ngxConfig: NgxDatatableConfig = { ...DEFAULT_NGX_DATATABLE_CONFIG };
  private hasEmittedFirstHigherLimit: boolean = false;
  private _data: any[] = [];
  public isMobileScreen: boolean = window.innerWidth < RESPONSIVE_CONFIG.MOBILE_BREAKPOINT;

  @Input({ required: true })
  set data(value: any[] | null) {
    if (!value || value.length === 0) {
      this._data = [];
      return;
    }

    this._data = [...value];
    this.hasEmittedFirstHigherLimit = false;

    const columnsToSum = (this.ngxConfig.columns ?? [])
      .filter((col) => (col as any).shouldSum)
      .map((col) => col.prop as string);

    if (columnsToSum.length > 0 && this._data.length > 0) {
      const totalRow: any = { isTotalRow: true };

      const firstVisibleColumn = (this.ngxConfig.columns ?? []).find(
        (c) => c.visible !== false
      );
      if (firstVisibleColumn?.prop) {
        totalRow[firstVisibleColumn.prop] = 'Total';
      } else {
        totalRow._showTotalInTemplate = true;
      }

      columnsToSum.forEach((prop) => {
        const sum = this.calculateSum(this._data, prop);
        totalRow[prop] = sum;
      });

      this.calculateUtilityGroup(
        totalRow,
        'shouldCalculateUtilityAmount',
        'shouldCalculateUtilityGain',
        'shouldCalculateUtility'
      );
      this.calculateUtilityGroup(
        totalRow,
        'shouldCalculateUtilityAmountBonus',
        'shouldCalculateUtilityGainBonus',
        'shouldCalculateUtilityBonus'
      );

      this._data.push(totalRow);
    }
  }

  get data(): any[] {
    return this._data;
  }

  @Input({ required: true })
  set config(value: Partial<NgxDatatableConfig> | null) {
    const defaultConfig = { ...DEFAULT_NGX_DATATABLE_CONFIG, ...value };

    defaultConfig.columns = (defaultConfig.columns ?? [])
      .filter((col: Partial<NgxDatatableColumn>) => col.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    this.ngxConfig = defaultConfig;
  }

  @Input() mobileActions: MobileCardAction[] = [];
  @Input() mobileCardTitleField: string = '';
  @Input() mobileCardSubtitleField: string = '';
  @Input() isLoading: boolean = false;
  @Input() disableMobileCards: boolean = false;

  get isAutoLoading(): boolean {
    return this.isLoading || this.ngxConfig.loadingIndicator;
  }

  get shouldShowMobileCards(): boolean {
    return this.isMobileScreen && !this.disableMobileCards;
  }

  @Output() public onSelectRow = new EventEmitter<any>();
  @Output() public onChangeSort = new EventEmitter<NgxDatatableSort>();
  @Output() public onChangeLimit = new EventEmitter<number>();
  @Output() public onChangePage = new EventEmitter<number>();
  @Output() public onButtonAction = new EventEmitter<{
    action: string;
    row: any;
  }>();

  constructor() {}

  public emitOnSelectRow(selectedRow: any): void {
    if (!selectedRow) return;
    this.onSelectRow.emit(selectedRow);
  }

  public emitOnSort(event: any): void {
    const sort = event.sorts?.[0];
    if (!sort) return;
    const { prop, dir } = sort;
    this.onChangeSort.emit({ prop, dir });
  }

  public emitOnChangeLimit(event: Event): void {
    const limit = (event.target as HTMLSelectElement).value;
    const formattedLimit = parseInt(limit, 10);
    this.ngxConfig.limit = formattedLimit;
    const shouldEmitLimit = this.shouldEmitLimit(
      this.ngxConfig.count,
      this.ngxConfig.limit
    );
    if (shouldEmitLimit) {
      this.onChangeLimit.emit(this.ngxConfig.limit);
    }
  }

  public emitOnChangePage($event: { page: number }): void {
    if (!$event.page) return;
    this.ngxConfig.page = $event.page;
    this.onChangePage.emit(this.ngxConfig.page);
  }

  public emitOnChangeLimitFromMobile(limit: number): void {
    this.ngxConfig.limit = limit;
    const shouldEmitLimit = this.shouldEmitLimit(
      this.ngxConfig.count,
      this.ngxConfig.limit
    );
    if (shouldEmitLimit) {
      this.onChangeLimit.emit(this.ngxConfig.limit);
    }
  }

  public emitOnChangePageFromMobile(page: number): void {
    this.ngxConfig.page = page;
    this.onChangePage.emit(this.ngxConfig.page);
  }

  public toggleExpandRow(ngxDatatable: DatatableComponent, row: any): void {
    ngxDatatable.rowDetail.toggleExpandRow(row);
  }

  private shouldEmitLimit(count: number, limit: number): boolean {
    return (
      this.shouldEmitOnLimitExceeded(count, limit) ||
      this.shouldEmitOnFirstLowerCount(count, limit)
    );
  }

  private shouldEmitOnLimitExceeded(count: number, limit: number): boolean {
    if (limit < count) {
      this.hasEmittedFirstHigherLimit = false;
      return true;
    }
    return false;
  }

  private shouldEmitOnFirstLowerCount(count: number, limit: number): boolean {
    if (count < limit && !this.hasEmittedFirstHigherLimit) {
      this.hasEmittedFirstHigherLimit = true;
      return true;
    }
    return false;
  }

  private calculateSum(data: any[], prop: string): string {
    const total = data.reduce((acc, row) => {
      const value = this.getNestedValue(row, prop);
      if (value == null || value === '') return acc;
      let num =
        typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
      if (isNaN(num)) return acc;
      return acc + num;
    }, 0);

    const isIntegerSum = this.ngxConfig.columns?.some(
      (col) => col.prop === prop && (col as any).shouldSumInteger
    );

    return isIntegerSum ? Math.round(total).toString() : total.toFixed(2);
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  private calculateUtilityGroup(
    totalRow: any,
    amountFlag: string,
    gainFlag: string,
    utilityFlag: string
  ): void {
    const columns = this.ngxConfig.columns ?? [];

    const amountCol = columns.find((col: any) => col[amountFlag]);
    const gainCol = columns.find((col: any) => col[gainFlag]);

    if (amountCol?.prop && gainCol?.prop) {
      const totalAmount = parseFloat(totalRow[amountCol.prop] || '0');
      const totalGain = parseFloat(totalRow[gainCol.prop] || '0');
      const profit = totalAmount - totalGain;
      const utilityValue =
        totalAmount > 0
          ? ((profit / totalAmount) * 100).toFixed(2) + ' %'
          : '0.00 %';

      columns
        .filter((col: any) => col[utilityFlag])
        .forEach((col: any) => {
          if (col.prop) {
            totalRow[col.prop] = utilityValue;
          }
        });
    }
  }

  public getFormattedValue(
    value: any,
    col: Partial<NgxDatatableColumn>,
    row: any
  ): string {
    if (col.valueFormatter) {
      return col.valueFormatter(value, row);
    }
    return typeof value === 'number' ? value.toFixed(2) : value;
  }

  public emitOnButtonAction(event: { action: string; row: any }): void {
    this.onButtonAction.emit(event);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.isMobileScreen = event.target.innerWidth < RESPONSIVE_CONFIG.MOBILE_BREAKPOINT;
  }

  get shouldApplyStickyHeader(): boolean {
    const result = this.data && this.data.length > 10;

    return result;
  }

  get datatableClass(): string {
    return this.shouldApplyStickyHeader ? 'material sticky-header' : 'material';
  }
}
